import React, { useEffect, useRef } from 'react';
import { Message } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedText from './AnimatedText';
import { useJarvisState } from '../../hooks/useJarvisState';

interface MessageListProps {
  messages: Message[];
}

const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { state } = useJarvisState();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div ref={containerRef} className="relative flex flex-col items-center min-h-full py-10">
      <AnimatePresence mode="popLayout">
        {messages.map((message, index) => {
          const isLatest = index === messages.length - 1;
          const isPrevious = index === messages.length - 2;
          const isOld = !isLatest && !isPrevious;
          
          return (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{
                opacity: isOld ? 0.2 : isLatest ? 1 : 0.5,
                y: 0,
                scale: isLatest ? 1 : isPrevious ? 0.92 : 0.85
              }}
              exit={{ opacity: 0, y: -20, scale: 0.8 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              style={{
                position: 'absolute',
                width: '100%',
                top: `${50 + (messages.length - index - 1) * -40}%`,
                transform: `translateY(-50%)`,
                pointerEvents: isLatest ? 'auto' : 'none'
              }}
              className="flex flex-col items-center px-4"
            >
              {message.type === 'user' ? (
                <div className="text-text-secondary mb-1 text-center max-w-xl text-sm">
                  {message.content}
                </div>
              ) : (
                <div className={`text-white font-light text-center max-w-2xl transition-all duration-500
                  ${isLatest ? 'text-lg' : isPrevious ? 'text-base' : 'text-sm'}`}>
                  {isLatest ? (
                    <AnimatedText 
                      text={message.content}
                      isSpeaking={state === 'speaking'}
                      className={`text-white font-light ${
                        isLatest ? 'text-lg' : isPrevious ? 'text-base' : 'text-sm'
                      }`}
                    />
                  ) : (
                    message.content
                  )}
                </div>
              )}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: isLatest ? 0.4 : 0.2 }}
                className="text-text-muted text-[10px] mt-1"
              >
                {new Date(message.timestamp).toLocaleTimeString()}
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;